/**
 * Incident Repository - Data access layer for incidents
 * Epic 6: Quality/Incidents/Exceptions
 */

import api from "../../../shared/api.jsx";

const INCIDENTS_ENDPOINTS = {
  base: (jobId) => `/orders/${jobId}/incidents`,
  byId: (jobId, incidentId) => `/orders/${jobId}/incidents/${incidentId}`,
  status: (jobId, incidentId) => `/orders/${jobId}/incidents/${incidentId}/status`,
  evidence: (jobId, incidentId) => `/orders/${jobId}/incidents/${incidentId}/evidence`,
  responsibility: (jobId, incidentId) => `/orders/${jobId}/incidents/${incidentId}/responsibility`,
};

/**
 * Get all incidents for a job
 * @param {string} jobId - Job identifier
 * @returns {Promise<Object>} API response
 */
export const fetchIncidentsByJobId = async (jobId) => {
  const response = await api.get(INCIDENTS_ENDPOINTS.base(jobId));
  return {
    success: response.success !== false,
    data: response.data || response,
  };
};

/**
 * Get a single incident by ID
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @returns {Promise<Object>} API response
 */
export const fetchIncidentById = async (jobId, incidentId) => {
  const response = await api.get(INCIDENTS_ENDPOINTS.byId(jobId, incidentId));
  return {
    success: response.success !== false,
    data: response.data || response,
  };
};

/**
 * Create a new incident
 * @param {string} jobId - Job identifier
 * @param {Object} incidentData - Incident data
 * @returns {Promise<Object>} API response
 */
export const createIncident = async (jobId, incidentData) => {
  const response = await api.post(INCIDENTS_ENDPOINTS.base(jobId), incidentData);
  return response;
};

/**
 * Update incident status
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {string} status - New status
 * @param {Object} context - Context (actorId, reason)
 * @returns {Promise<Object>} API response
 */
export const updateIncidentStatus = async (jobId, incidentId, status, context = {}) => {
  const response = await api.patch(INCIDENTS_ENDPOINTS.status(jobId, incidentId), {
    status,
    ...context,
  });
  return response;
};

/**
 * Add evidence to an incident
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {FormData} evidenceData - Evidence file and metadata
 * @returns {Promise<Object>} API response
 */
export const addIncidentEvidence = async (jobId, incidentId, evidenceData) => {
  const response = await api.post(
    INCIDENTS_ENDPOINTS.evidence(jobId, incidentId),
    evidenceData
  );
  return response;
};

/**
 * Attribute responsibility for an incident
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {Object} attributionData - Attribution data (responsibility, actorId, reason)
 * @returns {Promise<Object>} API response
 */
export const attributeResponsibility = async (jobId, incidentId, attributionData) => {
  const response = await api.post(
    INCIDENTS_ENDPOINTS.responsibility(jobId, incidentId),
    attributionData
  );
  return response;
};
